/**
 * In-Store Member Management Module
 * Handles member creation, authentication, and receipt submission
 */

// ============================================================================
// CONFIGURATION MODULE
// ============================================================================

/**
 * Configuration constants for the In-Store application
 */
const CONFIG = Object.freeze({
  API_ENDPOINTS: {
    CHECK_CREATE_MEMBER: "/apps/api/check-create-member",
    SUBMIT_RECEIPT: "/apps/api/submit-receipt",
    SUBMIT_A_RECEIPT_CONTENTS: "/apps/api/submit-receipt-metadata",
  },
  FILE_VALIDATION: {
    MAX_FILES: 5,
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB in bytes
    MAX_TOTAL_SIZE: 10 * 1024 * 1024, // 10MB total
    ALLOWED_TYPES: ["image/jpeg", "image/jpg", "image/png", "application/pdf"],
    ALLOWED_EXTENSIONS: ["jpg", "jpeg", "png", "pdf"],
  },
  SELECTORS: {
    CONSENT_CHECKBOX: "#consent",
    CONSENT_LABEL: ".consent-label",
    CUSTOMER_EMAIL_DISPLAY: "#customer_email",
    CONSENT_CHECK_ERROR: ".consent-check-error",
    CONSENT_CHECKBOX_WRAPPER: ".consent-checkbox-wrapper",
    EMAIL_FORM_WRAPPER: ".email-form-wrapper",
    EMAIL_INPUT: "#email",
    ERROR_INVALID_EMAIL: ".error-invalid-email",
    FILE_INPUT_WRAPPER: ".file-input-wrapper",
    FILE_INPUT_LABEL: ".file-input-label",
    FILE_INPUT: 'input[type="file"]',
    FILE_RULES: ".file-rules",
    FILE_UPLOAD_ERROR: ".file-upload-error",
    GREETING: ".greeting",
    HEADING: ".heading",
    NEXT_BTN: ".next-btn",
    INPUT_FILE_BTN: ".input-file-btn",
    JOIN_BTN: ".join-btn",
    SHIMMER: ".shimmer",
    THANK_YOU_CONTENT: ".thank-you-content",
    UPLOAD_RECEIPT_WRAPPER: ".upload-receipt-wrapper",
    UPLOAD_BUTTON: ".upload-btn",
    UPLOAD_RECEIPT_MESSAGE: ".upload-receipt-message",
    OPTIN_WELCOME: ".optin-welcome-section",
    REDIRECT_BTN: ".redirect-btn",
    SECTIONS: {
      OPTIN: ".optin",
      WELCOME: ".welcome",
      INSTRUCTIONS: ".instructions-section",
      THANKYOU: ".thankyou-section",
    },
  },
  CLASSES: {
    SUCCESS: "success",
    ERROR: "error",
    HIDDEN: "hidden",
    LOADING: "loading",
  },
  TIMEOUTS: {
    SUCCESS_RESET: 2000,
    ERROR_RESET: 3000,
  },
});

// ============================================================================
// STATE MANAGEMENT MODULE
// ============================================================================

/**
 * Application state management module
 */
const StateManager = (() => {
  let state = {
    user: {
      customerId: null,
      email: null,
    },
    ui: {
      isLoading: false,
      modalOpen: false,
    },
    files: {
      selected: [],
      processing: false,
    },
    errosr: {},
  };

  return {
    /**
     * Get current state
     * @returns {Object} Current application state
     */
    getState() {
      return { ...state };
    },

    /**
     * Reset user state
     */
    resetUser() {
      state.user = {
        customerId: null,
        email: null,
      };
    },

    /**
     * Set user data
     * @param {Object} userData - User data object
     */
    setUser(userData) {
      state.user = {
        customerId: userData.member_id || userData.customerId,
        email: userData.email || userData.email_id,
      };
    },

    /**
     * Update UI state
     * @param {Object} uiUpdates - UI state updates
     */
    updateUI(uiUpdates) {
      state.ui = { ...state.ui, ...uiUpdates };
    },

    /**
     * Update files state
     * @param {Object} filesUpdates - Files state updates
     */
    updateFiles(filesUpdates) {
      state.files = { ...state.files, ...filesUpdates };
    },

    /**
     * Get user data
     * @returns {Object} User data
     */
    getUser() {
      return { ...state.user };
    },
  };
})();

// ============================================================================
// UTILITY MODULES
// ============================================================================

/**
 * DOM utility functions module
 */
const DOMUtils = (() => {
  return {
    /**
     * Get element by selector with error handling
     * @param {string} selector - CSS selector
     * @param {boolean} required - Whether element is required
     * @returns {HTMLElement|null} Found element or null
     */
    getElement(selector, required = false) {
      const element = document.querySelector(selector);
      if (required && !element) {
        console.error(`Required element not found: ${selector}`);
      }
      return element;
    },

    /**
     * Get all elements by selector
     * @param {string} selector - CSS selector
     * @returns {NodeList} Found elements
     */
    getElements(selector) {
      return document.querySelectorAll(selector);
    },

    /**
     * Hide element by selector
     * @param {string} selector
     */
    hideElement(selector) {
      const element = this.getElement(selector);
      if (element) {
        element.classList.add(CONFIG.CLASSES.HIDDEN);
      } else {
        console.warn(`Element not found for hiding: ${selector}`);
      }
    },

    /**
     * Show loading state on button
     * @param {HTMLElement} button - Button element
     * @param {string} loadingText - Loading text
     */
    showButtonLoading(button, loadingText = "Processing...") {
      if (!button) return;

      button.disabled = true;
      button.dataset.originalText = button.textContent;
      button.classList.add(CONFIG.CLASSES.LOADING);
      button.style.opacity = 0.5;
    },

    /**
     * Show user notification
     * @param {string} message - Notification message
     * @param {string} type - Notification type ('success', 'error', 'info')
     * @param {string} selector - Selector for notification container
     */
    showNotification(
      message = "Something went wrong!",
      type = "info",
      selector = null,
    ) {
      if (!selector) {
        // For now, using alert - can be replaced with custom notification system
        alert(message);
        return;
      }

      const element = this.getElement(selector);

      // clear previous notifications
      this.clearNotification(selector);

      // Create new notification element

      const notificationElement = document.createElement("div");

      notificationElement.className = `notification ${type}`;
      notificationElement.textContent = message;
      element.appendChild(notificationElement);
    },

    /**
     *
     * @param {string} selector Selector for the element containing the notification
     */
    clearNotification(selector) {
      const element = this.getElement(selector);

      if (element) {
        const notification = element.querySelector(".notification");
        if (notification) {
          notification.remove();
        }
      }
    },

    /**
     * Update customer email display
     * @param {string} email - Customer email
     */
    showCustomerEmail(email) {
      const displayElement = this.getElement(
        CONFIG.SELECTORS.CUSTOMER_EMAIL_DISPLAY,
      );
      if (displayElement) {
        displayElement.textContent = email;
      }
    },

    /**
     * show screen by selector
     * @param {string} selector - Selector for the screen to show
     */
    showScreen(selector) {
      const sections = CONFIG.SELECTORS.SECTIONS;

      Object.values(sections).forEach((section) => {
        const sectionElement = DOMUtils.getElement(section);

        if (selector === section) {
          sectionElement.classList.remove(CONFIG.CLASSES.HIDDEN);
        } else {
          DOMUtils.hideElement(section);
        }
      });
    },
    /**
     * Show instruction screen
     */
    async showInstructionScreen() {
      DOMUtils.showScreen(CONFIG.SELECTORS.SECTIONS.INSTRUCTIONS);
      DOMUtils.hideElement(CONFIG.SELECTORS.OPTIN_WELCOME);

      const fileRules = DOMUtils.getElement(CONFIG.SELECTORS.FILE_RULES);
      const inputFileBtn = DOMUtils.getElement(CONFIG.SELECTORS.INPUT_FILE_BTN);

      try {
        // get SUBMIT A RECEIPT CONTENTS API
        const submitAReceiptContents =
          await MemberAPIService.getSubmitAReceiptContents();
        const { extra_config } = submitAReceiptContents.data;

        const fileUploadWrapper = DOMUtils.getElement(
          CONFIG.SELECTORS.UPLOAD_RECEIPT_WRAPPER,
        );
        if (fileUploadWrapper) {
          fileUploadWrapper.dataset.countErrorMessage =
            extra_config.file_count_error_message;
          fileUploadWrapper.dataset.sizeErrorMessage =
            extra_config.file_size_error_message;
          fileUploadWrapper.dataset.thankYouMessage =
            extra_config.thank_you_text;
        }

        const { replaceFrom, replaceTo } = window?.inStore?.merchantName;

        const replacedText = extra_config.instruction_text.replaceAll(
          `{{${replaceFrom}}}`,
          replaceTo,
        );
        fileRules.innerHTML = replacedText;
        inputFileBtn.classList.add("flex");
      } catch (error) {
        console.log(
          `Failed to fetch submit a receipt contents: ${error.message}`,
        );
        DOMUtils.hideElement(CONFIG.SELECTORS.SHIMMER);
        DOMUtils.showNotification(
          "Failed to load instructions. Please try again later.",
          "error",
          CONFIG.SELECTORS.FILE_UPLOAD_ERROR,
        );
      }
    },

    hideInstructionScreen() {
      DOMUtils.hideElement(CONFIG.SELECTORS.SECTIONS.INSTRUCTIONS);
    },

    showThankYouScreen() {
      DOMUtils.showScreen(CONFIG.SELECTORS.SECTIONS.THANKYOU);

      const thankYouWrapper = DOMUtils.getElement(
        CONFIG.SELECTORS.THANK_YOU_CONTENT,
      );

      const uploadReceiptMessage = DOMUtils.getElement(
        CONFIG.SELECTORS.UPLOAD_RECEIPT_WRAPPER,
      );

      if (thankYouWrapper) {
        thankYouWrapper.innerHTML =
          uploadReceiptMessage.dataset.thankYouMessage ||
          "Thank you for submitting your receipt!";
      }
    },
    /**
     * truncate text to a specified length
     */
    truncateText(text, length = 100) {
      if (text.length <= length) return text;
      return text.slice(0, length) + "...";
    },

    /**
     * Show/Hide consent error message
     * @param {boolean} show - Whether to show or hide the error
     * @returns {void}
     *
     **/
    consentErrorMessage(show = false) {
      const wrapper = DOMUtils.getElement(
        CONFIG.SELECTORS.CONSENT_CHECKBOX_WRAPPER,
      );
      const errorElement = DOMUtils.getElement(
        CONFIG.SELECTORS.CONSENT_CHECK_ERROR,
      );

      const errorMessage = wrapper.dataset.consentErrorMessage;

      this.showNotification(
        errorMessage,
        "error",
        CONFIG.SELECTORS.CONSENT_CHECK_ERROR,
      );
    },
  };
})();

/**
 * Validation utility functions module
 */
const ValidationUtils = (() => {
  return {
    /**
     * Validate email format
     * @param {string} email - Email to validate
     * @returns {boolean} Validation result
     */
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    /**
     * Validate individual file
     * @param {File} file - File to validate
     * @throws {Error} Validation error
     */
    validateFile(file) {
      const { ALLOWED_TYPES, ALLOWED_EXTENSIONS, MAX_FILE_SIZE } =
        CONFIG.FILE_VALIDATION;

      // Check file type
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (
        !ALLOWED_TYPES.includes(file.type) ||
        !ALLOWED_EXTENSIONS.includes(fileExtension)
      ) {
        throw new Error(
          JSON.stringify({
            ALLOWED_TYPES: `Invalid file type: ${file.name}. Only JPG, JPEG, PNG, and PDF files are allowed.`,
          }),
        );
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        throw new Error(
          JSON.stringify({
            MAX_FILE_SIZE: `File too large: ${file.name} (${fileSizeMB}MB). Maximum size per file is 10MB.`,
          }),
        );
      }
    },

    /**
     * Validate file collection
     * @param {FileList|File[]} files - Files to validate
     * @throws {Error} Validation error
     */
    validateFiles(files) {
      const fileArray = Array.from(files);
      const { MAX_FILES, MAX_TOTAL_SIZE } = CONFIG.FILE_VALIDATION;

      if (fileArray.length === 0) {
        throw new Error("Please select at least one file.");
      }

      if (fileArray.length > MAX_FILES) {
        throw new Error(
          JSON.stringify({
            MAX_FILES: `Maximum ${MAX_FILES} files allowed. Please select fewer files.`,
          }),
        );
      }

      // Calculate total size
      const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
        throw new Error(
          JSON.stringify({
            MAX_TOTAL_SIZE: `Total file size too large: ${totalSizeMB}MB. Maximum total size is 10MB.`,
          }),
        );
      }

      // Validate each file
      fileArray.forEach((file) => this.validateFile(file));
    },

    /**
     * Get file size in MB
     * @param {number} sizeInBytes - File size in bytes
     * @returns {string} Formatted size string
     */
    formatFileSize(sizeInBytes) {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(2)}MB`;
    },
  };
})();

// ============================================================================
// API SERVICE MODULES
// ============================================================================

/**
 * API service for member management
 */
const MemberAPIService = (() => {
  /**
   * Make API request with error handling
   * @param {string} endpoint - API endpoint
   * @param {Object} payload - Request payload
   * @returns {Promise<Object>} API response
   */
  async function makeAPIRequest(endpoint, payload) {
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.message ||
            `API call failed with status: ${response.status}`,
        );
      }

      if (!responseData.success) {
        throw new Error(responseData.message || "API returned error response");
      }

      return responseData;
    } catch (error) {
      console.error("API request failed:", {
        endpoint,
        payload,
        error: error.message,
      });
      throw error;
    }
  }

  return {
    /**
     * Check or create member
     * @param {string} emailId - Email address
     * @returns {Promise<Object>} API response
     */
    async checkCreateMember(emailId) {
      if (!emailId || !ValidationUtils.isValidEmail(emailId)) {
        throw new Error("Valid email address is required");
      }

      const payload = {
        email_id: emailId.trim().toLowerCase(),
      };

      //   console.log("Making API call to check/create member:", payload);

      const response = await makeAPIRequest(
        CONFIG.API_ENDPOINTS.CHECK_CREATE_MEMBER,
        payload,
      );

      //   console.log("Member check/create successful:", response);
      return response;
    },

    /**
     * Submit receipt files
     * @param {string} customerId - Customer ID
     * @param {Array<string>} receiptFiles - Base64 encoded files
     * @returns {Promise<Object>} API response
     */
    async submitReceipt(customerId, receiptFiles) {
      if (!customerId || typeof customerId !== "string") {
        throw new Error("Valid customer ID is required");
      }

      if (!Array.isArray(receiptFiles) || receiptFiles.length === 0) {
        throw new Error("At least one receipt file is required");
      }

      const payload = {
        customerId: customerId.trim(),
        receipt_files: receiptFiles,
      };

      //   console.log("Submitting receipt:", {
      //     customerId: payload.customerId,
      //     fileCount: receiptFiles.length,
      //   });

      const response = await makeAPIRequest(
        CONFIG.API_ENDPOINTS.SUBMIT_RECEIPT,
        payload,
      );

      //   console.log("Receipt submission successful:", response);
      return response;
    },

    async getSubmitAReceiptContents() {
      try {
        const response = await fetch(
          CONFIG.API_ENDPOINTS.SUBMIT_A_RECEIPT_CONTENTS,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          },
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.message || `API call failed with status: ${response.status}`,
          );
        }

        return data;
      } catch (error) {
        console.error("Failed to fetch submit a receipt contents:", error);
        throw error;
      }
    },
  };
})();

// ============================================================================
// FILE PROCESSING MODULE
// ============================================================================

/**
 * File processing utilities module
 */
const FileProcessingService = (() => {
  return {
    /**
     * Convert file to base64
     * @param {File} file - File to convert
     * @returns {Promise<string>} Base64 data URL
     */
    async fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () =>
          reject(new Error(`Failed to read file: ${file.name}`));
        reader.readAsDataURL(file);
      });
    },

    /**
     * Process multiple files to base64
     * @param {FileList|File[]} files - Files to process
     * @returns {Promise<Array<string>>} Array of base64 data URLs
     */
    async processFiles(files) {
      ValidationUtils.validateFiles(files);

      const fileArray = Array.from(files);
      const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);

      //   console.log("Processing files:", {
      //     count: fileArray.length,
      //     totalSize: ValidationUtils.formatFileSize(totalSize),
      //   });

      StateManager.updateFiles({ processing: true });

      try {
        const processedFiles = await Promise.all(
          fileArray.map(async (file, index) => {
            const result = await this.fileToBase64(file);
            // console.log(`File ${index + 1} processed:`, {
            //   name: file.name,
            //   size: ValidationUtils.formatFileSize(file.size),
            //   type: file.type,
            // });
            return result;
          }),
        );

        StateManager.updateFiles({
          processing: false,
          selected: processedFiles,
        });
        return processedFiles;
      } catch (error) {
        StateManager.updateFiles({ processing: false });
        throw error;
      }
    },

    /**
     * Get file information
     * @param {FileList|File[]} files - Files to analyze
     * @returns {Object} File information
     */
    getFileInfo(files) {
      const fileArray = Array.from(files);
      const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);

      return {
        count: fileArray.length,
        totalSize: ValidationUtils.formatFileSize(totalSize),
        files: fileArray.map((file) => ({
          name: file.name,
          truncateFileName: DOMUtils.truncateText(file.name, 20),
          size: ValidationUtils.formatFileSize(file.size),
          type: file.type,
        })),
      };
    },
  };
})();

// ============================================================================
// EVENT HANDLER MODULES
// ============================================================================

/**
 * Member join event handlers
 */
const MemberHandlers = (() => {
  /**
   * get invalid email error message
   * @return {string} Error message for invalid email
   */
  function getInvalidEmailErrorMessage() {
    const invalidEmailWrapper = DOMUtils.getElement(
      CONFIG.SELECTORS.ERROR_INVALID_EMAIL,
    );

    return (
      invalidEmailWrapper.dataset.notificationMessage ||
      "Please enter a valid email address."
    );
  }

  /**
   * Show error message for invalid email
   * @returns {void}
   */
  function showInvalidEmailError() {
    DOMUtils.showNotification(
      getInvalidEmailErrorMessage(),
      "error",
      CONFIG.SELECTORS.ERROR_INVALID_EMAIL,
    );
  }
  return {
    /**
     * Handle member join process
     * @param {HTMLElement} button - Join button element
     */
    async handleJoinSubmit(button) {
      const emailInput = DOMUtils.getElement(
        CONFIG.SELECTORS.EMAIL_INPUT,
        true,
      );

      const consentCheckbox = DOMUtils.getElement(
        CONFIG.SELECTORS.CONSENT_CHECKBOX,
      );

      const consentLabel = DOMUtils.getElement(CONFIG.SELECTORS.CONSENT_LABEL);

      // after successful join, show file upload wrapper
      const customerEmailDisplay = DOMUtils.getElement(
        CONFIG.SELECTORS.CUSTOMER_EMAIL_DISPLAY,
      );

      // clear previous notifications
      DOMUtils.clearNotification(CONFIG.SELECTORS.ERROR_INVALID_EMAIL);
      DOMUtils.clearNotification(CONFIG.SELECTORS.CONSENT_CHECK_ERROR);

      if (!emailInput) {
        console.error("Email input not found");
        return;
      }

      const email = emailInput.value.trim();

      // Validation
      if (!email || !ValidationUtils.isValidEmail(email)) {
        return showInvalidEmailError();
      }

      if (consentCheckbox && !consentCheckbox.checked) {
        DOMUtils.consentErrorMessage(true);
        consentLabel.classList.add("animate-shake");
        setTimeout(() => {
          consentLabel.classList.remove("animate-shake");
        }, 1200);
        return;
      }

      // Show loading state
      DOMUtils.showButtonLoading(button, "Processing...");

      try {
        const result = await MemberAPIService.checkCreateMember(email);

        // Update application state
        StateManager.setUser({
          customerId: result.customer_id,
          email: email,
        });

        // Update UI
        DOMUtils.showCustomerEmail(email);

        // console.log("Member join successful:", result);

        // Show greeting message
        DOMUtils.showScreen(CONFIG.SELECTORS.SECTIONS.WELCOME);
      } catch (error) {
        console.error("Member join failed:", error.message);

        const userMessage = error.message.includes("Invalid email")
          ? "Please enter a valid email address #1."
          : "Failed to process request. Please try again.";

        DOMUtils.showNotification(
          userMessage,
          "error",
          CONFIG.SELECTORS.ERROR_INVALID_EMAIL,
        );
      }
    },

    /**
     * Handle email input keypress
     * @param {KeyboardEvent} event - Keyboard event
     * @param {HTMLElement} submitButton - Submit button element
     */
    handleEmailKeypress(event, submitButton) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (submitButton) {
          this.handleJoinSubmit(submitButton);
        }
      }
    },
  };
})();

/**
 * File upload event handlers
 */
const FileHandlers = (() => {
  return {
    /**
     * Handle file selection
     * @param {FileList} files - Selected files
     */
    async handleFileSelection(files) {
      if (!files || files.length === 0) return;

      // clear previous notification
      DOMUtils.clearNotification(CONFIG.SELECTORS.FILE_UPLOAD_ERROR);
      try {
        // Validate files
        ValidationUtils.validateFiles(files);

        // Get file information
        const fileInfo = FileProcessingService.getFileInfo(files);

        // console.log("Files selected:", fileInfo);

        // Store files for later processing
        StateManager.updateFiles({ selected: files });

        // Show upload button
        const uploadButton = DOMUtils.getElement(
          CONFIG.SELECTORS.UPLOAD_BUTTON,
        );

        const fileInputLabel = DOMUtils.getElement(
          CONFIG.SELECTORS.FILE_INPUT_LABEL,
        );

        const inputFileButton = DOMUtils.getElement(
          CONFIG.SELECTORS.INPUT_FILE_BTN,
        );

        inputFileButton && inputFileButton.classList.remove("tb:inline-flex");
        fileInputLabel.textContent = fileInfo.files
          .map((file) => file.truncateFileName)
          .join(", ");

        uploadButton.classList.remove(CONFIG.CLASSES.HIDDEN);
      } catch (error) {
        const errorMessages = DOMUtils.getElement(
          CONFIG.SELECTORS.UPLOAD_RECEIPT_WRAPPER,
        );

        const errorElement = DOMUtils.getElement(
          CONFIG.SELECTORS.FILE_UPLOAD_ERROR,
        );
        console.log(error);
        console.error("File validation failed:", error.message);
        const parseErrorMessage = JSON.parse(error.message);

        const key = Object.keys(parseErrorMessage)[0];

        const errorMessageMap = {
          MAX_FILES:
            errorMessages.dataset.countErrorMessage ||
            `Maximum ${CONFIG.FILE_VALIDATION.MAX_FILES} files allowed. Please select fewer files.`,
          MAX_TOTAL_SIZE:
            errorMessages.dataset.sizeErrorMessage ||
            `Total file size too large: ${totalSizeMB}MB. Maximum total size is 10MB.`,
          MAX_FILE_SIZE:
            errorMessages.dataset.sizeErrorMessage ||
            `File too large: ${file.name} (${fileSizeMB}MB). Maximum size per file is 10MB.`,
        };

        DOMUtils.showNotification(
          errorMessageMap[key] ||
            "Invalid file selection. Please check your files and try again.",
          "error",
          CONFIG.SELECTORS.FILE_UPLOAD_ERROR,
        );

        // Clear the input
        const fileInput = DOMUtils.getElement(CONFIG.SELECTORS.FILE_INPUT);
        if (fileInput) {
          fileInput.value = "";
        }

        // TODO: reset the upload button label with its initial value
      }
    },

    /**
     * Handle receipt submission
     * @param {FileList} files - Files to submit
     * @param {HTMLElement} button - Submit button
     */
    async handleReceiptSubmission(files, button = null) {
      const user = StateManager.getUser();

      try {
        // Show processing state
        DOMUtils.showButtonLoading(button, "Processing files...");

        // Process files
        const processedFiles = await FileProcessingService.processFiles(files);

        // Show upload state
        DOMUtils.showButtonLoading(button, "Uploading...");

        // Submit receipt
        const result = await MemberAPIService.submitReceipt(
          user.customerId,
          processedFiles,
        );

        // show thank you screen
        DOMUtils.showThankYouScreen();
        return result;
      } catch (error) {
        console.error("Receipt submission failed:", error.message);

        const userMessage = error.message.includes("customer")
          ? "Invalid customer information. Please try again."
          : error.message.includes("file")
            ? "Invalid file format or size. Please check your files and try again."
            : "Failed to submit receipt. Please try again.";

        DOMUtils.showNotification(userMessage, "error");
        return null;
      }
    },
  };
})();

// ============================================================================
// MAIN APPLICATION MODULE
// ============================================================================

/**
 * Main application controller
 */
const InStoreApplication = (() => {
  let isInitialized = false;

  return {
    /**
     * Initialize the application
     */
    init() {
      if (isInitialized) {
        console.warn("InStore application already initialized");
        return;
      }

      this.bindEvents();
      this.setupFileUpload();

      isInitialized = true;
    },

    /**
     * Bind event listeners
     */
    bindEvents() {
      const joinBtn = DOMUtils.getElement(CONFIG.SELECTORS.JOIN_BTN);
      const emailInput = DOMUtils.getElement(CONFIG.SELECTORS.EMAIL_INPUT);
      const uploadButton = DOMUtils.getElement(CONFIG.SELECTORS.UPLOAD_BUTTON);
      const showInstructionBtn = DOMUtils.getElement(CONFIG.SELECTORS.NEXT_BTN);

      const redirect = DOMUtils.getElement(CONFIG.SELECTORS.REDIRECT_BTN);

      // Join button click handler
      if (joinBtn) {
        joinBtn.addEventListener("click", (event) => {
          event.preventDefault();
          MemberHandlers.handleJoinSubmit(joinBtn);
        });
      }

      if (uploadButton) {
        // Upload button click handler
        uploadButton.addEventListener("click", async (event) => {
          event.preventDefault();

          const files = StateManager.getState().files.selected;

          await FileHandlers.handleReceiptSubmission(files, uploadButton);
        });
      }

      if (showInstructionBtn) {
        showInstructionBtn.addEventListener("click", (event) => {
          event.preventDefault();

          DOMUtils.showInstructionScreen();
        });
      }

      if (redirect) {
        redirect.addEventListener("click", (event) => {
          event.preventDefault();
          const { href, pathname } = window.location;
          window.location.href = href.replace(
            pathname,
            redirect.dataset.redirectUrl,
          );
        });
      }
      // Email input keypress handler
      if (emailInput && joinBtn) {
        emailInput.addEventListener("keypress", (event) => {
          MemberHandlers.handleEmailKeypress(event, joinBtn);
        });
      }
    },

    /**
     * Setup file upload functionality
     */
    setupFileUpload() {
      const fileInput = DOMUtils.getElement(CONFIG.SELECTORS.FILE_INPUT);

      if (fileInput) {
        // Configure file input
        fileInput.setAttribute(
          "accept",
          CONFIG.FILE_VALIDATION.ALLOWED_EXTENSIONS.map(
            (ext) => `.${ext}`,
          ).join(","),
        );
        fileInput.setAttribute("multiple", "true");

        // Handle file selection
        fileInput.addEventListener("change", (event) => {
          FileHandlers.handleFileSelection(event.target.files);
        });
      }
    },

    /**
     * Get application state
     * @returns {Object} Current application state
     */
    getState() {
      return StateManager.getState();
    },

    /**
     * Reset application state
     */
    // reset() {
    //   StateManager.resetUser();
    //   StateManager.updateFiles({ selected: [], processing: false });
    //   StateManager.updateUI({
    //     isLoading: false,
    //     modalOpen: false,
    //   });
    // },
  };
})();

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize application when DOM is ready
 */
document.addEventListener("DOMContentLoaded", () => {
  InStoreApplication.init();
});
